import React, { useState, useRef, useEffect } from "react";
import { api } from "../lib/apiClient";
import { normalizeFloors } from "../lib/blueprint";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Sparkles, X, Send, Loader2, Bot, User as UserIcon, CheckCircle2, Zap } from "lucide-react";
import { toast } from "sonner";

const GREETING = {
  role: "assistant",
  content: "Hi! I'm your design assistant. Tell me what you want to build — e.g. \"design a 3 bed, 2 bath single-story ranch\" — and I'll draw it. If I need more details, I'll ask.",
  questions: [],
};

export default function AIAssistant({ open, onClose, context, onApplyFloors, onRunTool }) {
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    const history = messages.filter((m) => m !== GREETING).map((m) => ({ role: m.role, content: m.content }));
    const next = [...messages, { role: "user", content }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const start = await api.post("/ai/assistant", { message: content, history, context });
      const taskId = start.data.task_id;
      const d = await pollTask(taskId);
      let note = "";
      if (d.apply && Array.isArray(d.floors) && d.floors.length) {
        const flrs = normalizeFloors(d);
        onApplyFloors(flrs, d, null);
        note = `Applied ${flrs.length} floor(s) to your canvas.`;
        toast.success(note);
      }
      const toolNotes = [];
      if (onRunTool && Array.isArray(d.actions)) {
        for (const a of d.actions) {
          try { const r = await onRunTool(a.tool, a.params || {}); if (r) toolNotes.push(r); }
          catch { toolNotes.push(`Couldn't run ${a.tool}`); }
        }
      }
      setMessages((m) => [...m, { role: "assistant", content: d.reply || "Done.", questions: d.questions || [], applied: !!note, note, toolNotes }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: e.message || e.response?.data?.detail || "Sorry, I hit an error. Please try again.", questions: [] }]);
    } finally {
      setLoading(false);
    }
  };

  const pollTask = async (taskId) => {
    const deadline = Date.now() + 180000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2500));
      const res = await api.get(`/ai/assistant-status/${taskId}`);
      if (res.data.status === "completed") return res.data.result;
      if (res.data.status === "failed") throw new Error(res.data.error || "Assistant failed, please retry");
    }
    throw new Error("Timed out — please try again.");
  };

  if (!open) return null;

  return (
    <div className="absolute top-0 right-0 h-full w-full sm:w-[380px] z-40 flex flex-col glass border-l border-[#30363D] bg-[#0D1117]/95 animate-slide-in-right" data-testid="ai-assistant-panel">
      <div className="h-14 shrink-0 flex items-center gap-2 px-4 border-b border-[#30363D]">
        <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#A855F7]/15 text-[#A855F7]"><Sparkles className="w-4 h-4" /></span>
        <div className="flex-1">
          <div className="font-heading font-semibold text-sm text-[#F0F6FC]">AI Assistant</div>
          <div className="text-[10px] text-[#6E7681] font-mono-cad">designs & answers · asks when unsure</div>
        </div>
        <button onClick={onClose} data-testid="assistant-close-btn" className="text-[#8B949E] hover:text-[#F0F6FC] p-1"><X className="w-4 h-4" /></button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="assistant-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <span className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center ${m.role === "user" ? "bg-[#38BDF8]/15 text-[#38BDF8]" : "bg-[#A855F7]/15 text-[#A855F7]"}`}>
              {m.role === "user" ? <UserIcon className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
            </span>
            <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-[#38BDF8] text-[#0D1117]" : "bg-[#161B22] border border-[#30363D] text-[#F0F6FC]"}`}>
              {m.applied && (
                <div className="flex items-center gap-1.5 text-[#34D399] text-xs font-medium mb-1"><CheckCircle2 className="w-3.5 h-3.5" /> {m.note}</div>
              )}
              <div className="whitespace-pre-wrap">{m.content}</div>
              {m.toolNotes?.length > 0 && (
                <div className="mt-2 flex flex-col gap-1">
                  {m.toolNotes.map((t, ti) => (
                    <div key={ti} className="flex items-center gap-1.5 text-[#38BDF8] text-xs" data-testid={`assistant-tool-note-${ti}`}><Zap className="w-3 h-3 shrink-0" /> {t}</div>
                  ))}
                </div>
              )}
              {m.questions?.length > 0 && (
                <div className="mt-2 flex flex-col gap-1.5">
                  {m.questions.map((q, qi) => (
                    <button key={qi} onClick={() => send(q)} data-testid={`assistant-question-${qi}`}
                      className="text-left text-xs rounded-lg border border-[#A855F7]/40 bg-[#A855F7]/5 hover:bg-[#A855F7]/15 text-[#C9D1D9] px-2.5 py-1.5 transition-colors">
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-2" data-testid="assistant-loading">
            <span className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center bg-[#A855F7]/15 text-[#A855F7]"><Bot className="w-3.5 h-3.5" /></span>
            <div className="rounded-xl px-3 py-2 bg-[#161B22] border border-[#30363D] text-[#8B949E] text-sm flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
            </div>
          </div>
        )}
      </div>

      <div className="shrink-0 p-3 border-t border-[#30363D]">
        <div className="flex items-center gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            placeholder="Describe or ask for a change…" disabled={loading}
            className="bg-[#161B22] border-[#30363D] h-10" data-testid="assistant-input" />
          <Button onClick={() => send()} disabled={loading || !input.trim()} data-testid="assistant-send-btn"
            className="h-10 w-10 p-0 bg-[#A855F7] hover:bg-[#9333EA] text-white shrink-0">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
